import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Cropper from 'react-easy-crop';
import { FormattedMessage } from '../../util/reactIntl';
import { Button, Modal } from '../../components';
import { getCroppedImg, blobToFile } from '../../util/imageCrop';

import css from './ImageCropModal.module.css';

/**
 * ImageCropModal - A modal component for cropping images
 * 
 * @component
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the modal
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onManageDisableScrolling - Callback to manage page scrolling
 * @param {string} props.imageSrc - The source URL of the image to crop
 * @param {string} props.imageFileName - The original file name
 * @param {Function} props.onCropComplete - Callback when cropping is complete, receives cropped File
 * @param {number} [props.aspect] - Aspect ratio for cropping (default: 1 for square)
 * @param {string} [props.shape] - Shape of the crop area ('rect' or 'round')
 * @returns {JSX.Element}
 */
const ImageCropModal = props => {
  const {
    id,
    isOpen,
    onClose,
    onManageDisableScrolling,
    imageSrc,
    imageFileName,
    onCropComplete,
    aspect = 1,
    shape = 'round',
    className,
    rootClassName,
  } = props;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  const onCropChange = useCallback(crop => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback(zoom => {
    setZoom(zoom);
  }, []);

  const onRotationChange = useCallback(e => {
    setRotation(Number(e.target.value));
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!croppedAreaPixels || !imageSrc) {
      return;
    }

    setIsCropping(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      const croppedFile = blobToFile(croppedBlob, imageFileName);
      onCropComplete(croppedFile);
      onClose();
    } catch (e) {
      console.error('Error cropping image:', e);
    } finally {
      setIsCropping(false);
    }
  }, [croppedAreaPixels, imageSrc, imageFileName, rotation, onCropComplete, onClose]);

  const handleClose = useCallback(() => {
    // Reset state when closing
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    onClose();
  }, [onClose]);

  const classes = classNames(rootClassName || css.root, className);

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      className={classes}
    >
      <div className={css.modalContent}>
        <div className={css.header}>
          <h2 className={css.title}>
            <FormattedMessage id="ImageCropModal.title" />
          </h2>
        </div>

        <div className={css.cropContainer}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            cropShape={shape}
            showGrid={true}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
          />
        </div>

        <div className={css.controls}>
          <div className={css.controlGroup}>
            <label className={css.label}>
              <FormattedMessage id="ImageCropModal.zoom" />
            </label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={e => onZoomChange(Number(e.target.value))}
              className={css.slider}
            />
          </div>

          <div className={css.controlGroup}>
            <label className={css.label}>
              <FormattedMessage id="ImageCropModal.rotation" />
            </label>
            <input
              type="range"
              min={0}
              max={360}
              step={1}
              value={rotation}
              onChange={onRotationChange}
              className={css.slider}
            />
          </div>
        </div>

        <div className={css.actions}>
          <Button
            onClick={handleClose}
            className={css.cancelButton}
            disabled={isCropping}
          >
            <FormattedMessage id="ImageCropModal.cancel" />
          </Button>
          <Button
            onClick={handleCropConfirm}
            className={css.confirmButton}
            inProgress={isCropping}
            disabled={isCropping}
          >
            <FormattedMessage id="ImageCropModal.apply" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ImageCropModal.defaultProps = {
  aspect: 1,
  shape: 'round',
  className: null,
  rootClassName: null,
};

ImageCropModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  imageSrc: PropTypes.string,
  imageFileName: PropTypes.string,
  onCropComplete: PropTypes.func.isRequired,
  aspect: PropTypes.number,
  shape: PropTypes.oneOf(['rect', 'round']),
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default ImageCropModal;
